import React, { useState, useEffect, useRef } from "react";
import Sidebar from "./Sidebar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faUsers,
  faArrowRight,
  faLightbulb,
  faChartLine,
  faShieldAlt,
  faClock,
  faCheckCircle,
  faGraduationCap,
  faUserGraduate,
  faUsersRectangle,
  faCertificate,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import "./dashboard.css";
import "./addStudents.css";

const AddStudents = () => {
  const navigate = useNavigate();
  const [stats] = useState({
    totalEnrolled: 1245,
    activeFamilies: 342,
    successRate: "98.5%",
    avgProcessingTime: "2.3 min",
  });

  const [hoveredCard, setHoveredCard] = useState(null);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const enrollmentOptions = [
    {
      id: 1,
      title: "Individual Enrollment",
      description:
        "Register a single student with comprehensive academic, personal, and contact information using our detailed form.",
      icon: faUserGraduate,
      badge: "Standard",
      buttonText: "Add Solo Student",
      path: "/soloStudent",
      iconBg: "linear-gradient(135deg, #2E2A74 0%, #1E1A5E 100%)",
      iconColor: "#FFFFFF",
      features: [
        "Complete profile creation",
        "Academic history tracking",
        "Real-time ID generation",
      ],
    },
    {
      id: 2,
      title: "Family / Group Enrollment",
      description:
        "Efficiently add multiple siblings or group members under a shared family profile with centralized management.",
      icon: faUsersRectangle,
      badge: "Recommended",
      buttonText: "Add Family/Group",
      path: "/familyStudent",
      iconBg: "linear-gradient(135deg, #FFF200 0%, #FFD600 100%)",
      iconColor: "#2E2A74",
      features: [
        "Shared family information",
        "Multiple student management",
        "Bulk operations support",
      ],
    },
  ];

  const guidanceItems = [
    {
      id: 1,
      title: "Data Accuracy",
      description:
        "Ensure all information is accurate and up-to-date to maintain institutional data integrity.",
      icon: faShieldAlt,
    },
    {
      id: 2,
      title: "Quick Processing",
      description:
        "Our streamlined process ensures student enrollment is completed in under 3 minutes.",
      icon: faClock,
    },
    {
      id: 3,
      title: "Quality Assurance",
      description:
        "All entries are automatically validated for consistency and compliance with standards.",
      icon: faCheckCircle,
    },
  ];

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <div className="dashboard-wrapper add-students-portal-wrapper">
      <Sidebar />

      <div className="main-content">
        {/* Hero Section */}
        <div className="add-students-hero-section fade-in-up">
          <h1 className="add-students-main-title">Student Enrollment Portal</h1>
          <p className="add-students-hero-description">
            Choose your preferred enrollment method to securely add new students
            to our institutional database with precision and efficiency.
          </p>
        </div>

        {/* Horizontal Square Cards */}
        <div
          ref={sectionRef}
          className={`add-students-cards-container ${
            isVisible
              ? "add-students-scroll-reveal visible"
              : "add-students-scroll-reveal"
          }`}
        >
          {enrollmentOptions.map((option) => (
            <div
              key={option.id}
              className={`add-students-card pulse-on-hover ${
                hoveredCard === option.id ? "add-students-active" : ""
              }`}
              onClick={() => handleNavigation(option.path)}
              onMouseEnter={() => setHoveredCard(option.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{ animationDelay: `${option.id * 0.2}s` }}
              role="button"
              tabIndex="0"
              aria-label={`Select ${option.title} enrollment method`}
            >
              {/* Card Badge */}
              <div className="add-students-card-badge">{option.badge}</div>

              {/* Decorative Corner */}
              <div className="add-students-card-corner"></div>

              {/* Icon Container */}
              <div
                className="add-students-icon-circle"
                style={{ background: option.iconBg }}
              >
                <FontAwesomeIcon
                  icon={option.icon}
                  style={{
                    fontSize: "2.5rem",
                    color: option.iconColor,
                  }}
                />
              </div>

              {/* Card Content */}
              <div className="add-students-card-content">
                <h3 className="add-students-card-title">{option.title}</h3>

                <p className="add-students-card-description">
                  {option.description}
                </p>

                {/* Features List */}
                <div className="add-students-card-features">
                  {option.features.map((feature, index) => (
                    <div key={index} className="add-students-feature-item">
                      <div className="add-students-feature-icon">
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          style={{
                            color: option.id === 1 ? "#2E2A74" : "#FFF200",
                            fontSize: "0.75rem",
                          }}
                        />
                      </div>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Button Container */}
              <div className="add-students-button-container">
                <button
                  className={`add-students-option-btn ${
                    option.id === 1
                      ? "add-students-btn-individual"
                      : "add-students-btn-family"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigation(option.path);
                  }}
                  aria-label={`Proceed with ${option.title}`}
                >
                  {option.buttonText}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    style={{
                      fontSize: "0.875rem",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ROW CONTAINER FOR STATS + GUIDANCE */}
        <div className="add-students-row-section">
          {/* Performance Stats Banner */}
          <div
            className="add-students-stats-banner fade-in-up"
            style={{ animationDelay: "0.4s" }}
          >
            <div className="add-students-stats-header">
              <div className="add-students-stats-icon">
                <FontAwesomeIcon
                  icon={faChartLine}
                  style={{
                    color: "#2E2A74",
                    fontSize: "1.5rem",
                  }}
                />
                <h3 className="add-students-stats-title">Enrollment Performance</h3>
              </div>
            </div>

            <div className="add-students-stats-grid">
              <div className="add-students-stat-item">
                <div className="add-students-stat-number">
                  {stats.totalEnrolled.toLocaleString()}
                </div>
                <div className="add-students-stat-label">Total Enrolled</div>
              </div>
              <div className="add-students-stat-item">
                <div className="add-students-stat-number">
                  {stats.activeFamilies}
                </div>
                <div className="add-students-stat-label">Active Families</div>
              </div>
              <div className="add-students-stat-item">
                <div className="add-students-stat-number">{stats.successRate}</div>
                <div className="add-students-stat-label">Success Rate</div>
              </div>
              <div className="add-students-stat-item">
                <div className="add-students-stat-number">
                  {stats.avgProcessingTime}
                </div>
                <div className="add-students-stat-label">Avg. Processing Time</div>
              </div>
            </div>
          </div>

          {/* Guidance Section */}
          <div
            className="add-students-guidance-section fade-in-up"
            style={{ animationDelay: "0.6s" }}
          >
            <div className="add-students-guidance-header">
              <div className="add-students-guidance-icon">
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <h3 className="add-students-guidance-title">
                Enrollment Best Practices
              </h3>
            </div>

            <div className="add-students-guidance-grid">
              {guidanceItems.map((item) => (
                <div key={item.id} className="add-students-guidance-item">
                  <h4>
                    <FontAwesomeIcon icon={item.icon} />
                    {item.title}
                  </h4>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="add-students-footer-note">
          <div className="add-students-footer-content">
            <FontAwesomeIcon icon={faGraduationCap} />
            <span>
              Need assistance? Contact our enrollment support team at
              enrollment@myschool.edu
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStudents;